import { objectResolver } from '../app.component';
import { IMSObject, IMSId } from './ims-object';
import { Servicejob } from './servicejob';
import { format } from 'date-fns';


/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

export class TourStop extends IMSObject {
  static fromDto(dto: TourStopDTO, databag?: any) {
    const obj = new this(
      {
        imsId: dto.id,
        secondaryId: dto.secondaryid,
        objRef: 'ims/TourStop',
      },
      dto.label,
      dto.tour
        ? new IMSObject(dto.tour.id, undefined, dto.tour.label, 'ims/Tour')
        : undefined
    );

    if (databag) {
      obj.databag = databag;
    } else {
      obj.databag = dto;
    }
    return obj;
  }

  constructor(
    public readonly id: IMSId,
    label: string,
    public tour: IMSObject
  ) {
    super(id.imsId, id.secondaryId, label, 'ims/TourStop');
  }

  public get sjidlist(): string {
    if (this.databag['sjidlist']) {
      return this.databag['sjidlist'];
    }
    return undefined;
  }

  public get servicejobIds(): IMSId[] {
    if (this.databag['sjidlist'] == undefined) {
      return [];
    }

    let idStr: string = this.databag['sjidlist'];
    return idStr.split('_').map((id) => {
      return {
        imsId: +id,
        secondaryId: undefined,
        objRef: 'ims/ServiceJob',
      };
    });
  }

  public get branchidlist(): string {
    if (this.databag['sjidlist']) {
      return this.databag['sjidlist'];
    }
    return undefined;
  }

  public get branchIds(): IMSId[] {
    if (this.databag['branchidlist'] == undefined) {
      return [];
    }

    let idStr: string = this.databag['branchidlist'];
    return idStr.split('_').map((id) => {
      return {
        imsId: +id,
        secondaryId: undefined,
        objRef: 'ims/Branch',
      };
    });
  }

  public get needsCashing(): boolean {
    for (const sjobId of this.servicejobIds) {
      const sjob = objectResolver.getObject(sjobId) as Servicejob;
      if (sjob.cashing) {
        return true;
      }
    }

    return false;
  }

  public get cashingTranslationKey(): string {
    if (this.servicejobIds?.length > 0) {
      const sjob = objectResolver.getObject(
        this.servicejobIds[0]
      ) as Servicejob;
      return sjob.cashingTranslationKey;
    }

    return '';
  }

  public calculateDeliveryWindow(servicejobs: Servicejob[]): string {
    let earliestArrival = Number.POSITIVE_INFINITY;
    let latestArrival = Number.NEGATIVE_INFINITY;
    for (const sjob of servicejobs) {
      if (sjob.arrivalEarliest) {
        earliestArrival = Math.min(earliestArrival, sjob.arrivalEarliest);
      }
      if (sjob.arrivalAlternativeEarliest) {
        earliestArrival = Math.min(
          earliestArrival,
          sjob.arrivalAlternativeEarliest
        );
      }

      if (sjob.arrivalLatest) {
        latestArrival = Math.max(latestArrival, sjob.arrivalLatest);
      }
      if (sjob.arrivalAlternativeLatest) {
        latestArrival = Math.max(latestArrival, sjob.arrivalAlternativeLatest);
      }
    }

    if (
      earliestArrival != Number.POSITIVE_INFINITY &&
      latestArrival != Number.NEGATIVE_INFINITY
    ) {
      return (
        format(new Date(earliestArrival), 'HH:mm') +
        ' - ' +
        format(new Date(latestArrival), 'HH:mm')
      );
    } else {
      return undefined;
    }
  }

  public fetchDeliveryNotes(servicejobs: Servicejob[]): string {
    let deliveryNotes: string[] = [];
    for (const sjob of servicejobs) {
      if (
        sjob.additionalinfotext != undefined &&
        sjob.additionalinfotext != ''
      ) {
        deliveryNotes.push(sjob.additionalinfotext);
      }
    }

    deliveryNotes = deliveryNotes.reduce((acc: string[], cur: string) => {
      if (!acc.includes(cur) && cur != undefined && cur != '') {
        acc.push(cur);
      }
      return acc;
    }, []);
    return deliveryNotes.join('<br>');
  }
}

export interface TourStopDTO {
  id: number;
  secondaryid: number;
  label: string;
  tour: {
    id: number;
    label: string;
  };
}
